// HAPPYCAP_AUTH_MOBILE_COMPACT_V1
// HAPPYCAP_AUTH_TYPO_UNIFIED_V1
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { registerUser } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import Footer from "../components/Footer";

const TEMP_EMAIL_DOMAINS = [
  "10minutemail.com", "tempmail.com", "mailinator.com", "guerrillamail.com",
  "yopmail.com", "discard.email", "temp-mail.org", "maildrop.cc"
];

function isTempEmail(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  return TEMP_EMAIL_DOMAINS.includes(domain);
}

// ✅ Validación teléfono: + seguido de 11 o 12 dígitos
function isTelefonoValido(tel: string): boolean {
  return /^\+\d{11,12}$/.test(tel);
}

export default function SignupMenor() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [telefono, setTelefono] = useState("");
  const [tutorNombre, setTutorNombre] = useState("");
  const [tutorRelacion, setTutorRelacion] = useState("");
  const [confirmLegal, setConfirmLegal] = useState(false);
  const [confirmResponsable, setConfirmResponsable] = useState(false);
  const [msg, setMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const navigate = useNavigate();

  const handleSignup = async () => {
    setMsg("");

    if (!email || !password || !confirmPassword || !telefono || !tutorNombre || !tutorRelacion) {
      setMsg(t("signup_minor.required_fields"));
      return;
    }

    if (isTempEmail(email)) {
      setMsg(t("signup_minor.temp_email"));
      return;
    }

    if (password !== confirmPassword) {
      setMsg(t("signup_minor.password_mismatch"));
      return;
    }

    if (!isTelefonoValido(telefono)) {
      setMsg(t("signup_minor.invalid_phone"));
      return;
    }

    if (!["padre", "madre", "tutor"].includes(tutorRelacion)) {
      setMsg(t("signup_minor.invalid_relation"));
      return;
    }

    if (!confirmLegal || !confirmResponsable) {
      setMsg(t("signup_minor.legal_required"));
      return;
    }

    setIsSubmitting(true);

    try {
      await registerUser({
        email,
        password,
        telefono,
        tipo_registro: "menor",
        acepta_mayoria_edad: true,
        acepta_politica_funcionamiento: true,
        acepta_responsabilidad_menor: true,
        nombre_tutor: tutorNombre,
        relacion_tutor: tutorRelacion,
        language: i18n.language || "es",
      });

      setMsg(t("signup_minor.success"));
      setTimeout(() => navigate("/activate", { state: { email } }), 2000);
    } catch (error) {
      console.error("Signup error:", error);
      setMsg("❌ " + (error.response?.data?.error || t("signup_minor.error")));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="w-[calc(100%-2.5rem)] max-w-sm sm:max-w-lg mx-auto mt-8 sm:mt-10 mb-10 bg-white border border-white/80 shadow-[0_16px_55px_rgba(22,63,99,0.09)] rounded-[24px] p-5 sm:p-9"
        role="form"
        aria-labelledby="signup-title"
      >
        <h2
          id="signup-title"
          className="text-2xl sm:text-4xl font-bold tracking-tight text-center mb-8 text-[#163F63]"
        >
          {t("signup_minor.title")}
        </h2>

        <div className="space-y-4">
          <input
            type="email"
            placeholder={t("signup_minor.email")}
            aria-label={t("signup_minor.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8]"
          />

          <div className="relative">
            <input
            type={showPassword ? "text" : "password"}
            placeholder={t("signup_minor.password")}
            aria-label={t("signup_minor.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#247BA8] hover:text-[#245580] focus:outline-none focus:ring-2 focus:ring-[#247BA8] rounded-lg p-1"
            >
              {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
            </button>
          </div>

          <div className="relative">
            <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder={t("signup_minor.confirm_password")}
            aria-label={t("signup_minor.confirm_password")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] pr-12"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((visible) => !visible)}
              aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              aria-pressed={showConfirmPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#247BA8] hover:text-[#245580] focus:outline-none focus:ring-2 focus:ring-[#247BA8] rounded-lg p-1"
            >
              {showConfirmPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
            </button>
          </div>

          {/* 📱 Campo de teléfono con tooltip */}
          <div className="relative">
            <input
              type="tel"
              placeholder={t("signup_minor.phone")}
              aria-label={t("signup_minor.phone")}
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(!isTelefonoValido(telefono))}
              className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] focus:ring-2 focus:ring-[#247BA8]"
            />
            {showTooltip && !isTelefonoValido(telefono) && (
              <span className="absolute left-0 top-full mt-1 bg-red-600 text-white text-xs rounded px-2 py-1">
                {t("signup_minor.tooltip_phone")} <br />
                <strong>{t("signup_minor.example_phone", { defaultValue: "Ej: +34911222333" })}</strong>
              </span>
            )}
          </div>

          <input
            type="text"
            placeholder={t("signup_minor.guardian_name")}
            aria-label={t("signup_minor.guardian_name")}
            value={tutorNombre}
            onChange={(e) => setTutorNombre(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8]"
          />

          <select
            aria-label={t("signup_minor.select_relation")}
            value={tutorRelacion}
            onChange={(e) => setTutorRelacion(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] text-gray-700"
          >
            <option value="">{t("signup_minor.select_relation")}</option>
            <option value="padre">{t("signup_minor.father")}</option>
            <option value="madre">{t("signup_minor.mother")}</option>
            <option value="tutor">{t("signup_minor.guardian")}</option>
          </select>

          <div className="text-sm text-gray-700 space-y-3">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={confirmLegal}
                onChange={(e) => setConfirmLegal(e.target.checked)}
                aria-label={t("signup_minor.confirm_legal_plain")}
                className="mt-1"
              />
              <span
                dangerouslySetInnerHTML={{
                  __html: t("signup_minor.confirm_legal"),
                }}
              />
            </label>

            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={confirmResponsable}
                onChange={(e) => setConfirmResponsable(e.target.checked)}
                aria-label={t("signup_minor.confirm_responsable")}
                className="mt-1"
              />
              <span>{t("signup_minor.confirm_responsable")}</span>
            </label>
          </div>

          {/* Botón */}
          <button
            onClick={handleSignup}
            disabled={isSubmitting}
            className={`w-full font-semibold py-3 rounded-xl transition shadow-md ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600 text-white"
            }`}
          >
            {isSubmitting
              ? t("signup_minor.submitting")
              : t("signup_minor.submit")}
          </button>
        </div>

        {msg && (
          <p
            className={`mt-4 text-center text-sm font-medium ${
              msg.includes("❌") ? "text-red-600" : "text-green-600"
            }`}
            role="alert"
            aria-live="polite"
          >
            {msg}
          </p>
        )}
      </div>
      <Footer loginStyle />
    </>
  );
}
